import { TransactionType } from '../proto/generated/order'
import { OrderBook } from '../proto/generated/security'

type OptionQuoteRef = {
    topAsk?: number
    topBid?: number
    ltp?: number
}

/**
 * Stale reference price stored on `TradeOrder.price` for premium/payoff display.
 * Order submission still sends price 0 for market orders.
 */
export function tradeOrderReferencePriceFromOption(
    transactionType: TransactionType,
    option?: OptionQuoteRef | null
): number {
    if (!option) return 0
    const sidePrice = transactionType === TransactionType.BUY ? option.topAsk : option.topBid
    if (sidePrice != null && sidePrice > 0) return sidePrice
    return option.ltp ?? 0
}

/**
 * Limit-order price in the same scale as order-book quotes and `TradeOrder.price`
 * (typically raw tick × multiplier). Uses top ask for buy and top bid for sell;
 * if that side is missing or zero, falls back to order book LTP, then snapshot LTP.
 */
export function limitPriceFromOrderBookOrLtp(
    orderBook: OrderBook | undefined,
    transactionType: TransactionType,
    snapshotLastTradedPrice?: number
): number {
    const isBuy = transactionType === TransactionType.BUY
    const sidePrice = isBuy ? orderBook?.asks?.[0]?.price : orderBook?.bids?.[0]?.price
    if (sidePrice != null && sidePrice > 0) return sidePrice
    const bookLtp = orderBook?.lastTradedPrice ?? 0
    if (bookLtp > 0) return bookLtp
    const snapLtp = snapshotLastTradedPrice ?? 0
    if (snapLtp > 0) return snapLtp
    return 0
}

/** Same as {@link limitPriceFromOrderBookOrLtp} using scalar top-of-book fields (fewer Redux re-renders). */
export function limitPriceFromTopOfBook(
    transactionType: TransactionType,
    topBid?: number,
    topAsk?: number,
    bookLtp?: number,
    snapshotLastTradedPrice?: number
): number {
    const isBuy = transactionType === TransactionType.BUY
    const sidePrice = isBuy ? topAsk : topBid
    if (sidePrice != null && sidePrice > 0) return sidePrice
    if (bookLtp != null && bookLtp > 0) return bookLtp
    const snapLtp = snapshotLastTradedPrice ?? 0
    if (snapLtp > 0) return snapLtp
    return 0
}
